import { Link } from "@tanstack/react-router";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { trackEvent } from "@/lib/tracking";

interface Photo {
  url: string;
  alt: string;
}

interface Props {
  photos?: Photo[];
}

const CAPTIONS = [
  "Collision repair — bumper and quarter panel, refinished in factory colour.",
  "Car-park scrape — sanded, filled, blended and lacquered to match.",
  "Dent removal & paint touch-up — finished so it's hard to tell where it was.",
];

/**
 * Bodywork "results" showcase. We don't always have paired before/after
 * shots, so this presents up to 3 finished jobs with a "Result" badge,
 * a short caption, and clear CTAs to send photos via WhatsApp.
 */
export function BodyworkBeforeAfter({ photos = [] }: Props) {
  const tiles = photos.slice(0, 3);
  if (tiles.length === 0) return null;

  return (
    <AnimatedSection>
      <section aria-labelledby="bodywork-results" className="section-y bg-secondary/40">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="max-w-2xl">
            <p className="eyebrow">Real workshop results</p>
            <h2 id="bodywork-results" className="mt-3 display-lg text-foreground">
              The work speaks for itself.
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl">
              Every bodywork job is finished to a high standard with professional colour matching.
              Here's a snapshot of recent repairs from the workshop.
            </p>
          </div>

          <StaggerContainer className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {tiles.map((p, i) => (
              <StaggerItem key={i}>
                <figure className="overflow-hidden rounded-2xl border border-border bg-card">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={p.url}
                      alt={p.alt}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                    />
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-background/90 backdrop-blur px-2.5 py-1 text-[11px] font-bold text-foreground">
                      <CheckCircle2 className="h-3 w-3 text-primary" /> Result
                    </span>
                  </div>
                  <figcaption className="p-4 text-sm text-muted-foreground leading-relaxed">
                    {CAPTIONS[i] ?? p.alt}
                  </figcaption>
                </figure>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-card p-5">
            <p className="text-sm text-foreground font-semibold">
              Got a scrape, dent or collision damage? Send us photos for a free written estimate.
            </p>
            <Link
              to="/gallery"
              onClick={() => trackEvent("gallery_open", { location: "bodywork_results" })}
              className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-primary/80"
            >
              See full gallery <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </AnimatedSection>
  );
}
